import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

const Notification = ({
  type = 'success',
  title = '',
  message = '',
  onClose,
  showCloseButton = true,
  className = '',
  iconSize = 'default',
  variant = 'default',
  autoClose = true,
  autoCloseDelay = 5000,
  ...props
}) => {

  // Color schemes for different types
  const colorSchemes = {
    success: {
      border: 'border-green-200',
      bg: 'bg-green-50',
      text: 'text-green-800',
      title: 'text-green-900',
      iconBg: 'bg-green-100'
    },
    error: {
      border: 'border-red-200',
      bg: 'bg-red-50',
      text: 'text-red-800',
      title: 'text-red-900',
      iconBg: 'bg-red-100'
    },
    warning: {
      border: 'border-yellow-200',
      bg: 'bg-yellow-50',
      text: 'text-yellow-800',
      title: 'text-yellow-900',
      iconBg: 'bg-yellow-100'
    },
    info: {
      border: 'border-blue-200',
      bg: 'bg-blue-50',
      text: 'text-blue-800',
      title: 'text-blue-900',
      iconBg: 'bg-blue-100'
    }
  };

  // Size variants - optimized for single notification (reduced height)
  const sizeVariants = {
    small: {
      container: 'p-2.5 sm:p-3',
      title: 'text-xs sm:text-sm font-semibold',
      message: 'text-xs sm:text-sm'
    },
    default: {
      container: 'p-3 sm:p-4',
      title: 'text-sm sm:text-base font-semibold',
      message: 'text-xs sm:text-sm'
    },
    large: {
      container: 'p-4 sm:p-5',
      title: 'text-base sm:text-lg font-semibold',
      message: 'text-sm sm:text-base'
    }
  };

  // Style variants
  const styleVariants = {
    default: 'rounded-lg border shadow-lg',
    elevated: 'rounded-xl border shadow-xl',
    flat: 'rounded-lg border-0 shadow-none',
    outlined: 'rounded-lg border-2 shadow-none'
  };

  const colors = colorSchemes[type] || colorSchemes.success;
  const sizes = sizeVariants[iconSize] || sizeVariants.default;
  const styles = styleVariants[variant] || styleVariants.default;

  // Auto-close functionality
  useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, onClose]);

  return (
    <div
      className={`
        relative flex items-start
        ${colors.bg} ${colors.border} ${colors.text}
        ${sizes.container} ${styles}
        ${className}
        transition-all duration-300 ease-in-out
        hover:shadow-xl
        backdrop-blur-sm
      `}
      style={{
        minWidth: '280px',
        maxWidth: '400px'
      }}
      {...props}
    >
      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className={`
            ${sizes.title} ${colors.title}
            mb-0.5
            break-words leading-tight
          `}>
            {title}
          </h3>
        )}
        
        {message && (
          <p className={`
            ${sizes.message} ${colors.text}
            break-words leading-relaxed
          `}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

// PropTypes for better development experience
Notification.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.string,
  onClose: PropTypes.func,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  iconSize: PropTypes.oneOf(['small', 'default', 'large']),
  variant: PropTypes.oneOf(['default', 'elevated', 'flat', 'outlined']),
  autoClose: PropTypes.bool,
  autoCloseDelay: PropTypes.number
};

export default Notification;
